/**
 * A brand's logo: choose, preview, save, remove.
 *
 * The picture is shown fitted (`contain`, never stretched) on a neutral
 * checker so a white or transparent logo is still visible. Saving sends the
 * original and a JPEG rendition (white background, longest side at most 512px)
 * in the shape the API takes. It saves on its own button, not with the profile
 * form: a file is not a field, and one failing must not hold the other back.
 */
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';

import { accentTokens, isAccentHex } from './accent.ts';
import { isApiClientError } from './client.ts';
import {
  LOGO_TYPES,
  RenditionError,
  checkLogoFile,
  logoBody,
  makeJpegRendition,
  logoProblemKey,
  logoServerErrorKey,
  type LogoBody,
  type RenditionCanvas,
} from './logo-file.ts';
import { kitText } from './messages.ts';
import { Callout, Field } from './ui.tsx';

function readDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('decode'));
    img.src = src;
  });
}

/** The JPEG rendition as a data URL; throws `RenditionError` if the browser cannot make one. */
async function jpegRendition(src: string): Promise<string> {
  const img = await loadImage(src);
  return makeJpegRendition(img, () => document.createElement('canvas') as unknown as RenditionCanvas);
}

interface Staged {
  readonly file: File;
  readonly dataUrl: string;
}

export function LogoUpload({
  brandName,
  accent,
  currentSrc,
  onSave,
  onRemove,
  disabled,
}: {
  brandName: string;
  /** The brand's colour, for the initial tile shown while there is no logo. */
  accent?: string | null;
  /** A loadable URL of the saved logo, or nothing. */
  currentSrc?: string | undefined;
  onSave: (body: LogoBody) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  disabled?: boolean;
}) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // The saved logo is shown until the reader has a staged one; after a save or
  // a removal the parent's reload brings the new `currentSrc`, but the answer
  // should not wait for it, so a removal hides the old picture at once.
  const [removed, setRemoved] = useState(false);
  useEffect(() => { setRemoved(false); }, [currentSrc]);

  const shown = staged ? staged.dataUrl : removed ? undefined : currentSrc;
  const initial = (brandName.trim().charAt(0) || '?').toUpperCase();
  const tileStyle: CSSProperties | undefined = accent && isAccentHex(accent)
    ? (() => {
        const tk = accentTokens(accent);
        return { background: tk.light.a, color: tk.light.aInk };
      })()
    : undefined;

  const choose = (file: File | undefined) => {
    if (!file) return;
    setDone(null);
    const bad = checkLogoFile(file);
    if (bad) {
      setStaged(null);
      setProblem(kitText(logoProblemKey(bad)));
      return;
    }
    setProblem(null);
    readDataUrl(file)
      .then((dataUrl) => loadImage(dataUrl).then(() => setStaged({ file, dataUrl })))
      .catch(() => { setStaged(null); setProblem(kitText('logo.error.unreadable')); });
  };

  const fail = (e: unknown) =>
    setProblem(kitText(
      e instanceof RenditionError ? 'logo.error.rendition' : logoServerErrorKey(isApiClientError(e) ? e.code : undefined),
    ));

  const save = () => {
    if (!staged) return;
    setBusy('save');
    setProblem(null);
    setDone(null);
    jpegRendition(staged.dataUrl)
      .then((jpeg) => onSave(logoBody(staged.file, staged.dataUrl, jpeg)))
      .then(() => {
        setStaged(null);
        setDone(kitText('logo.saved'));
        if (input.current) input.current.value = '';
      })
      .catch(fail)
      .finally(() => setBusy(null));
  };

  const remove = () => {
    setBusy('remove');
    setProblem(null);
    setDone(null);
    onRemove()
      .then(() => { setRemoved(true); setDone(kitText('logo.removed')); })
      .catch(fail)
      .finally(() => setBusy(null));
  };

  const off = disabled || busy !== null;
  const hasSaved = Boolean(currentSrc) && !removed;

  return (
    <Field group label={kitText('logo.label')} hint={kitText('logo.hint')}>
      <div className="logo-upload">
        <div
          className={`logo-upload-frame${shown ? '' : ' is-empty'}`}
          {...(shown ? {} : { style: tileStyle })}
        >
          {shown ? (
            <img src={shown} alt={staged ? kitText('logo.preview') : kitText('logo.alt', { brand: brandName })} />
          ) : (
            <span aria-hidden="true">{initial}</span>
          )}
        </div>
        <div className="logo-upload-side">
          <div className="logo-upload-actions">
            <input
              ref={input}
              id={inputId}
              className="sr-only"
              type="file"
              accept={LOGO_TYPES.join(',')}
              disabled={off}
              onChange={(e) => choose(e.target.files?.[0])}
            />
            <label
              htmlFor={inputId}
              className={`copy logo-upload-pick${off ? ' is-disabled' : ''}`}
            >
              {hasSaved || staged ? kitText('logo.replace') : kitText('logo.choose')}
            </label>
            {staged ? (
              <>
                <button type="button" className="primary" disabled={off} onClick={save}>
                  {busy === 'save' ? kitText('logo.uploading') : kitText('logo.save')}
                </button>
                <button
                  type="button"
                  className="link-button"
                  disabled={off}
                  onClick={() => { setStaged(null); setProblem(null); if (input.current) input.current.value = ''; }}
                >
                  {kitText('logo.discard')}
                </button>
              </>
            ) : hasSaved ? (
              <button type="button" className="link-button" disabled={off} onClick={remove}>
                {busy === 'remove' ? kitText('logo.removing') : kitText('logo.remove')}
              </button>
            ) : null}
          </div>
          {staged ? (
            <p className="panel-note">{kitText('logo.staged', { name: staged.file.name })}</p>
          ) : !shown ? (
            <p className="panel-note">{kitText('logo.none')}</p>
          ) : null}
        </div>
      </div>
      <div aria-live="polite">
        {problem ? <Callout tone="error">{problem}</Callout> : null}
        {done && !problem ? <Callout tone="note" fold={false}>{done}</Callout> : null}
      </div>
    </Field>
  );
}
